import { useContext, useEffect, useState } from "react";

import { downloadProjectDocument } from "../../../../../../../service/project";
import { getProjectDocuments, GetProjectDocumentsResponse } from "../../../../../../../service/publicquery";
import { Status } from "../../../../../../../service/Shared";
import { initiateDocumentDownloadViaBrowser } from "../../../../../../../utils";
import { Toast } from "../../../../../../../widget";
import { ProjectContext } from "../../../ProjectContext";

interface UsePreAccessDocumentsTabReturnData {
  documents: GetProjectDocumentsResponse[] | undefined;
  isLoading: boolean;
  /** The documents could not be loaded, so the tab must not claim the project has none. */
  hasError: boolean;
  downloadDocument: (projectDocumentHistoryUuid: string) => Promise<void>;
}

export const usePreAccessDocumentsTab = (): UsePreAccessDocumentsTabReturnData => {
  const { publicProjectDetails } = useContext(ProjectContext);
  const [documents, setDocuments] = useState<GetProjectDocumentsResponse[]>();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const downloadDocument = async (projectDocumentHistoryUuid: string): Promise<void> => {
    const res = await downloadProjectDocument({ projectDocumentHistoryUuid });

    initiateDocumentDownloadViaBrowser(res);
  };

  useEffect(() => {
    const onLoadFailed = (): void => {
      setHasError(true);
      Toast.error({ message: "Unable to load this project's documents" });
    };

    const fetchData = async (): Promise<void> => {
      setHasError(false);

      if (!publicProjectDetails) {
        setIsLoading(false);
        return;
      }

      await getProjectDocuments({
        projectUuid: publicProjectDetails.uuid,
        groupUuid: publicProjectDetails.group?.groupUuid || null,
      })
        .then((response) => {
          if (response.status === Status.Success) {
            setDocuments(response.data);
          } else {
            onLoadFailed();
          }
        })
        .finally(() => setIsLoading(false));
    };

    fetchData().catch(() => {
      onLoadFailed();
      setIsLoading(false);
    });
  }, [publicProjectDetails]);

  return {
    documents,
    isLoading,
    hasError,
    downloadDocument,
  };
};
